import { z } from "zod";

export const MemorySchema = z.object({
  id: z.string(),
  memory: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  user_id: z.string(),
  metadata: z
    .object({
      // `.nullish()`, not `.optional()`: a null on a declared key fails an
      // optional parse, and the caller's fallback (applyCatchToSchema) then
      // replaces the whole metadata object with "" — losing every key.
      datamate_id: z.string().nullish(),
      datamate_name: z.string().nullish(),
      // The API has served this as both a number and a quoted string
      // ("0.95") across releases — a strict number parse blanked every
      // memory list when the string form arrived. Accept both. `nullable`
      // short-circuits before coercion, so an explicit null stays null
      // rather than coercing to 0.
      confidence: z.coerce.number().nullish(),
      memory_type: z.string().nullish(),
      title: z.string().nullish(),
      // Which client wrote the record. Only altimate-code sets it, and only to
      // MIRROR_SOURCE; everything else leaves it absent. Declared rather than
      // left to `.passthrough()` so the delete path can branch on it with a
      // type instead of an index lookup.
      source: z.string().nullish(),
      // altimate-code's tombstone, written as the STRING "true" (see
      // `isArchived` in its memory-api.ts). Its own delete path archives rather
      // than deletes, so the record survives with this marker set.
      archived: z.string().nullish(),
      archived_at: z.string().nullish(),
    })
    // Preserve unknown keys: EditMemory spreads this parsed object into an
    // update that replaces metadata wholesale, so anything dropped here is
    // lost from the record permanently. Keys are `.nullish()` for the same
    // reason — a null on a declared key would fail the parse and send the
    // caller down a fallback that discards the whole object.
    .passthrough()
    .nullable(),
  categories: z.array(z.string()).nullish(),
});

export type Memory = z.infer<typeof MemorySchema>;

export const MemoryResponseSchema = z.object({
  message: z.string(),
});

export type MemoryResponse = z.infer<typeof MemoryResponseSchema>;

/** The `metadata.source` altimate-code stamps on every record it mirrors.
 *
 * Kept in step with `MIRROR_SOURCE` in altimate-code's
 * `workspace/memory-api.ts`. It is also the value `listMemories` must opt into
 * via `include_sources`, since the API hides these records by default. */
export const MIRROR_SOURCE = "altimate-code";

/** Was this memory mirrored from a local altimate-code block?
 *
 * Such a record is a COPY. The block on the author's disk is the authority —
 * altimate-code's sync abandons a push whose block no longer exists locally —
 * so deleting the copy while the block remains does not stick: the next sweep
 * acts on the block again. These are archived rather than deleted. */
export const isMirroredMemory = (memory: Memory): boolean =>
  memory.metadata?.source === MIRROR_SOURCE;

/** Has this memory been archived — by altimate-code's own delete path, or by
 * ours?
 *
 * The marker is altimate-code's convention alone: the backend and the memory
 * service know nothing about it, so a record archived in the CLI is still
 * returned by `/memory/list` and has to be filtered by every reader that does
 * not want it. */
export const isArchivedMemory = (memory: Memory): boolean =>
  // Coerced, not compared strictly to the string. altimate-code writes the
  // STRING "true" and `isArchived` there tests for it exactly, but metadata is
  // passthrough, so another writer could set a boolean — which would leave the
  // record visible here while the CLI still treats it as archived.
  String(memory.metadata?.archived) === "true";

/** The metadata an archive writes: the record's own, plus the marker.
 *
 * Spread, because the update endpoint replaces metadata WHOLESALE — anything
 * dropped here is gone from the record for good. That is also why the schema
 * above is `.passthrough()`. */
export const archivedMetadata = (memory: Memory): Record<string, unknown> => ({
  ...(memory.metadata ?? {}),
  archived: "true",
  archived_at: new Date().toISOString(),
});
